import autobind from 'class-autobind';

import BetType from 'atg/api/BetType';
import PointReduction from 'atg/api/PointReduction';
import * as device from 'atg/utils/device';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export default class PointReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, PointReductionView.prototype);
  }

  getGridClass() {
    return 'view-point-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-points', this.getNumber(PointReductionView));
  }

  getMenuTitle() {
    return Text.get('view-points', this.getNumber(PointReductionView));
  }

  getMenuGroupTitle() {
    return Text.get('view-group-points');
  }

  getMenuAddItemTitle() {
    return Text.get('view-points-add');
  }

  getMenuMaxNumberOfViews() {
    return this.getPointReductionSet().getMaxNumberOfPointReductions();
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- TOOLBAR ----------------------------------------

  hasToolbar() {
    return true;
  }

  getToolbarContent() {
    return {
      pointReductionIndex: this.index,
    };
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}
  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();
    const viewIndex = this.getIndex();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};
      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;

    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    if (betRow) {
      info.bottomBlue = betRow.getPointReductionPoints(
        position.column,
        viewIndex,
      );
    }

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const cell = this.getGrid().getCellByPosition(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      {
        icon: 'button-minus',
        onPress: this.onGridMinusButtonPressed,
      },
      {
        icon: 'button-plus',
        onPress: this.onGridPlusButtonPressed,
      },
      {
        icon: 'button-numpad',
        onPress: this.onGridNumpadButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    if (cell.hasClass('in-range') && !device.isTouch()) {
      this.onGridNumpadButtonPressed(position);
    }

    this.getGrid().showCellButtons(position, buttons);
  }

  onGridPlusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();

    if (willShowLockedAlert) {
      this.getGrid().closeHighlight();
    } else {
      const viewIndex = this.getIndex();
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const points = betRow.getPointReductionPoints(position.column, viewIndex);

      betRow.setPointReductionPoints(
        position.column,
        viewIndex,
        Math.min(points + 1, 999),
      );
      this.getGrid().updateRow(position.row);

      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridMinusButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();

    if (willShowLockedAlert) {
      this.getGrid().closeHighlight();
    } else {
      const viewIndex = this.getIndex();
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const points = betRow.getPointReductionPoints(position.column, viewIndex);

      betRow.setPointReductionPoints(
        position.column,
        viewIndex,
        Math.max(points - 1, 0),
      );
      this.getGrid().updateRow(position.row);

      this.getAPI().scheduleBetUpdate();
    }
  }

  onGridNumpadButtonPressed(position) {
    const willShowLockedAlert = this.showAlertWhenLocked();

    if (willShowLockedAlert) {
      this.getGrid().closeHighlight();
    } else {
      const viewIndex = this.getIndex();
      const betRow = this.getAPI().betInformation.getRow(position.row);
      const points = betRow.getPointReductionPoints(position.column, viewIndex);

      this.getGrid().showNumberInput(position, points);
    }
  }

  // ---------------------------------------- DATA ----------------------------------------

  getIndex() {
    return super.getIndex(PointReductionView);
  }

  getPointReduction() {
    const reductionSet = this.getPointReductionSet();
    let reduction;

    if (reductionSet) {
      const index = this.getIndex();
      const numberOfReductions = reductionSet.getNumberOfPointReductions();

      if (index < numberOfReductions) {
        reduction = reductionSet.getPointReduction(index);
      }
    }

    if (!reduction) {
      reduction = new PointReduction();
    }

    return reduction;
  }

  getPointReductionSet() {
    return this.getAPI()
      .betInformation.getReductionSet()
      .getPointReductionSet();
  }

  getItem() {
    return this.getPointReduction();
  }

  addItem() {
    return this.getPointReductionSet().addPointReduction();
  }

  deleteItem() {
    const api = this.getAPI();
    const index = this.getIndex();

    this.getPointReductionSet().deletePointReduction(index);

    const numberOfRows = api.betInformation.getNumberOfRows();
    for (let row = 0; row < numberOfRows; row++) {
      api.betInformation.getRow(row).deletePointReduction(index);
    }

    this.getAPI().scheduleBetUpdate();
  }

  getNumberOfItems() {
    const set = this.getPointReductionSet();
    return set ? set.getNumberOfPointReductions() : 0;
  }

  // ---------------------------------------- TEXT HELPER FUNCTIONS -------------------------

  isSingleRaceBetType() {
    const betTypeCode = this.getAPI().raceInformation.getBetType();
    return BetType.getBetType(betTypeCode).hasSingleRace();
  }

  getInText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-on')
      : Text.get('bottom-conditions-in');
  }

  getInMinText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-on-min')
      : Text.get('bottom-conditions-in-min');
  }

  getOfRacesText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-of-positions')
      : Text.get('bottom-conditions-of-races');
  }

  getPointsInRaceText() {
    return this.isSingleRaceBetType()
      ? Text.get('bottom-conditions-points-on-position')
      : Text.get('bottom-conditions-points-in-race');
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-point-reduction',
      {
        /*** Section 1 ***/
        '%point-reduction-sum%': Text.get(
          'bottom-conditions-point-reduction-sum',
        ),
        /*** Section 2 ***/
        '%point-reduction-factor%': Text.get(
          'bottom-conditions-point-reduction-factor',
        ),
        /*** Section 3 ***/
        '%point-reduction-number-winners%': Text.get(
          'bottom-conditions-point-reduction-number-winners',
        ),
        /*** Section 4 ***/
        '%point-reduction-miscellaneous%': Text.get(
          'bottom-conditions-point-reduction-miscellaneous',
        ),
      },
    ));

    $conditions.find('.point-sums h2').append(
      createHelpIcon({
        header: Text.get('help-points-reduction-points-header'),
        body: Text.get('help-points-reduction-points-body'),
      }),
    );

    $conditions.find('.factor-on-point-sums h2').append(
      createHelpIcon({
        header: Text.get('help-points-reduction-factor-header'),
        body: Text.get('help-points-reduction-factor-body'),
      }),
    );

    $conditions.find('.winners-in-intervals h2').append(
      createHelpIcon({
        header: Text.get('help-points-reduction-intervals-header'),
        body: Text.get('help-points-reduction-intervals-body'),
      }),
    );

    $conditions.find('.misc h2').append(
      createHelpIcon({
        header: Text.get('help-points-reduction-misc-header'),
        body: Text.get('help-points-reduction-misc-body'),
      }),
    );

    $conditions.find('form').event('submit', this.onFormSubmit);

    this.updateConditionsPointSums();
    this.updateConditionsFactorOnPointSums();
    this.updateConditionsWinnersInIntervals();
    this.updateConditionsMisc();

    return $conditions;
  }

  // ---------------------------------------- POINT SUMS ----------------------------------------

  updateConditionsPointSums() {
    const reduction = this.getPointReduction();
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const numberOfRows = Math.max(1, reduction.getNumberOfPointSums());

    const $wrapper = this.$conditions.find('.point-sums');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const pointSum = reduction.getPointSum(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-point-reduction-point-sum-row',
        {
          '%Min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%in-point-sum%': Text.get(
            'bottom-conditions-point-reduction-in-point-sum',
          ),
          '%in%': this.getInText(),
          '%of-races%': this.getOfRacesText(),
          '%num-races%': numRaces,
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row, 1);

      let legNumbers;

      if (pointSum) {
        $row
          .find('.min-points')
          .val(Utils.negativeBlankToValue(pointSum.getMinPoints()));
        $row
          .find('.max-points')
          .val(Utils.negativeBlankToValue(pointSum.getMaxPoints()));
        $row
          .find('.number-of-races')
          .val(pointSum.getNumberOfRaces() || numRaces);

        legNumbers = pointSum.getLegNumbers();
      } else {
        $row.find('.number-of-races').val(numRaces);
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);

    $wrapper.find('.button.plus').reevent('down', this.onAddPointSumPressed);
    $wrapper
      .find('.button.delete')
      .reevent('down', this.onDeletePointSumPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddPointSumPressed() {
    const reduction = this.getPointReduction();
    let pointSum;

    if (reduction.getNumberOfPointSums() === 0) {
      pointSum = reduction.addPointSum();
      pointSum.setLegNumbers(this.getLegNumbers());
    }

    pointSum = reduction.addPointSum();
    pointSum.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsPointSums();

    const $row = this.$conditions.find('.point-sums .rows .row:last-child');
    $row.hide();
    $row.slideDown(300);
  }

  onDeletePointSumPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getPointReduction();

    reduction.deletePointSum(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfPointSums() < 1) {
      this.updateConditionsPointSums();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsPointSums();
        }.bind(this),
      );
    }
  }

  // ------------------------------- FACTOR ON POINT SUMS ---------------------------------

  handleBetConfigUpdate() {
    this.updateConditionsFactorOnPointSums();
  }

  updateConditionsFactorOnPointSums() {
    const reduction = this.getPointReduction();
    const numberOfRows = Math.max(1, reduction.getNumberOfFactorsOnPointSums());

    const $wrapper = this.$conditions.find('.factor-on-point-sums');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const factorOnPointSum = reduction.getFactorOnPointSum(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-point-reduction-factor-on-point-sum-row',
        {
          '%rows-with-point-sum%': Text.get(
            'bottom-conditions-rows-with-point-sum',
          ),
          '%points%': Text.get('bottom-conditions-points'),
          '%should-be-submitted%': Text.get(
            'bottom-conditions-factor-to-be-submitted',
          ),
          '%times%': Text.get('bottom-conditions-factor-times'),
          '%from%': Text.get('bottom-conditions-from'),
          '%to%': Text.get('bottom-conditions-to'),
          '%row%': n,
        },
      );

      if (factorOnPointSum) {
        $row
          .find('.min-points')
          .val(Utils.negativeBlankToValue(factorOnPointSum.getMinPoints()));
        $row
          .find('.max-points')
          .val(Utils.negativeBlankToValue(factorOnPointSum.getMaxPoints()));
        const hasPointMinMax =
          factorOnPointSum.getMinPoints() >= 0 ||
          factorOnPointSum.getMaxPoints() >= 0;
        $row
          .find('.factor')
          .val(hasPointMinMax ? factorOnPointSum.getFactor() : '')
          .prop('disabled', !hasPointMinMax);
      }

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);

    $wrapper
      .find('.button.plus')
      .reevent('down', this.onAddFactorOnPointSumPressed);
    $wrapper
      .find('.button.delete')
      .reevent('down', this.onDeleteFactorOnPointSumPressed);

    const guaranteeValueNotDefault = this.getGuaranteeValue() !== 0;
    if (guaranteeValueNotDefault) {
      $wrapper.find('input[type=number]').prop('disabled', true);
    }

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddFactorOnPointSumPressed() {
    if (this.showAlertWhenGuaranteeSet()) {
      return;
    }
    const reduction = this.getPointReduction();
    if (reduction.getNumberOfFactorsOnPointSums() === 0) {
      reduction.addFactorOnPointSum();
    }
    reduction.addFactorOnPointSum();

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsFactorOnPointSums();

    const $row = this.$conditions.find(
      '.factor-on-point-sums .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteFactorOnPointSumPressed(event) {
    if (this.showAlertWhenGuaranteeSet()) {
      return;
    }
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getPointReduction();

    reduction.deleteFactorOnPointSum(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfFactorsOnPointSums() < 1) {
      this.updateConditionsFactorOnPointSums();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsFactorOnPointSums();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- WINNERS IN INTERVALS ----------------------------------------

  updateConditionsWinnersInIntervals() {
    const reduction = this.getPointReduction();
    const numberOfRows = Math.max(1, reduction.getNumberOfWinnersInIntervals());

    const $wrapper = this.$conditions.find('.winners-in-intervals');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const winnersInInterval = reduction.getWinnersInInterval(n);
      const $row = StickyTemplate.jq(
        'bottom-conditions-point-reduction-winners-in-interval-row',
        {
          '%From%': Text.get('bottom-conditions-From'),
          '%to%': Text.get('bottom-conditions-to'),
          '%points%': Text.get('bottom-conditions-points'),
          '%in-min%': this.getInMinText(),
          '%max%': Text.get('bottom-conditions-max'),
          '%of-races%': this.getOfRacesText(),
          '%points-in-race%': this.getPointsInRaceText(),
          '%row%': n,
        },
      );

      this.addNumberOfRacesInput($row);

      let legNumbers;

      if (winnersInInterval) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(winnersInInterval.getFromPoints()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(winnersInInterval.getToPoints()));
        $row
          .find('.min-number-of-races')
          .val(winnersInInterval.getMinNumberOfRaces());
        $row
          .find('.max-number-of-races')
          .val(winnersInInterval.getMaxNumberOfRaces());

        legNumbers = winnersInInterval.getLegNumbers();
      } else {
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);
      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper
      .find('.button.plus')
      .reevent('click', this.onAddWinnersInIntervalPressed);
    $wrapper
      .find('.button.delete')
      .reevent('click', this.onDeleteWinnersInIntervalPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddWinnersInIntervalPressed() {
    const reduction = this.getPointReduction();
    let winnersInInterval;

    if (reduction.getNumberOfWinnersInIntervals() === 0) {
      winnersInInterval = reduction.addWinnersInInterval();
      winnersInInterval.setLegNumbers(this.getLegNumbers());
    }

    winnersInInterval = reduction.addWinnersInInterval();
    winnersInInterval.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsWinnersInIntervals();

    const $row = this.$conditions.find(
      '.winners-in-intervals .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteWinnersInIntervalPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getPointReduction();
    reduction.deleteWinnersInInterval(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfWinnersInIntervals() < 1) {
      this.updateConditionsWinnersInIntervals();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsWinnersInIntervals();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- MISC ----------------------------------------

  updateConditionsMisc() {
    const reduction = this.getPointReduction();
    const numberOfRows = Math.max(1, reduction.getNumberOfMiscs());

    const $wrapper = this.$conditions.find('.misc');
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const misc = reduction.getMisc(n);

      const $row = StickyTemplate.jq(
        'bottom-conditions-point-reduction-misc-row',
        {
          '%from%': Text.get('bottom-conditions-from'),
          '%to%': Text.get('bottom-conditions-to'),
          '%points-in-race%': this.getPointsInRaceText(),
          '%row%': n,
        },
      );

      this.addMiscRequireModeInput($row);
      this.addMiscTypeInput($row);

      let legNumbers;

      if (misc) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(misc.getFromPoints()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(misc.getToPoints()));

        $row.find('.misc-require-mode').val(misc.getRequireMode());
        $row.find('.misc-type').val(misc.getType());

        legNumbers = misc.getLegNumbers();
      } else {
        legNumbers = this.getLegNumbers();
      }

      this.addLegNumbersInputs($row, legNumbers);

      $rows.append($row);
    }

    $wrapper
      .find('input[type=number]')
      .reevent('input', this.onConditionsChanged);
    $wrapper
      .find('input:not([type=number]), select')
      .reevent('change', this.onConditionsChanged);
    $wrapper.find('.button.plus').reevent('click', this.onAddMiscPressed);
    $wrapper.find('.button.delete').reevent('click', this.onDeleteMiscPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddMiscPressed() {
    const reduction = this.getPointReduction();
    let misc;

    if (reduction.getNumberOfMiscs() === 0) {
      misc = reduction.addMisc();
      misc.setLegNumbers(this.getLegNumbers());
    }

    misc = reduction.addMisc();
    misc.setLegNumbers(this.getLegNumbers());

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsMisc();

    const $row = this.$conditions.find('.misc .rows .row:last-child');
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteMiscPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const index = $row.index();

    const reduction = this.getPointReduction();

    reduction.deleteMisc(index);
    this.getAPI().scheduleBetUpdate();

    if (reduction.getNumberOfMiscs() < 1) {
      this.updateConditionsMisc();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsMisc();
        }.bind(this),
      );
    }
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  onConditionsChanged() {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getPointReduction();

    let numberOfFormRows = formData.pointSums ? formData.pointSums.length : 0;
    const numberOfPointSums = reduction.getNumberOfPointSums();

    let n;

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfPointSums) {
        reduction.addPointSum();
      }

      const pointSum = reduction.getPointSum(n);
      const pointSumData = formData.pointSums[n];

      pointSum.setMinPoints(Utils.valueToNegativeBlank(pointSumData.minPoints));
      pointSum.setMaxPoints(Utils.valueToNegativeBlank(pointSumData.maxPoints));
      pointSum.setNumberOfRaces(pointSumData.numberOfRaces);
      pointSum.setLegNumbers(pointSumData.legNumbers);
    }

    while (numberOfFormRows < reduction.getNumberOfPointSums()) {
      reduction.deletePointSum(reduction.getNumberOfPointSums() - 1);
    }

    numberOfFormRows = formData.factorOnPointSums
      ? formData.factorOnPointSums.length
      : 0;
    const numberOfFactorsOnPointSums =
      reduction.getNumberOfFactorsOnPointSums();

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfFactorsOnPointSums) {
        reduction.addFactorOnPointSum();
      }

      const factorOnPointSum = reduction.getFactorOnPointSum(n);
      const factorOnPointSumData = formData.factorOnPointSums[n];

      const numericFormValueMinPoints = Utils.valueToNegativeBlank(
        factorOnPointSumData.minPoints,
      );
      const numericFormValueMaxPoints = Utils.valueToNegativeBlank(
        factorOnPointSumData.maxPoints,
      );
      factorOnPointSum.setMinPoints(numericFormValueMinPoints);
      factorOnPointSum.setMaxPoints(numericFormValueMaxPoints);
      const hasPointMinMax =
        numericFormValueMinPoints >= 0 || numericFormValueMaxPoints >= 0;
      factorOnPointSum.setFactor(
        hasPointMinMax ? factorOnPointSumData.factor : 1,
      );

      // Depending on hasPointMinMax, adjust factor input field
      const $factorInput = $(
        `.factor-on-point-sums input[name="factorOnPointSums[${n}][factor]"]`,
      );
      if ($factorInput) {
        $factorInput.prop('disabled', !hasPointMinMax);
        if (!hasPointMinMax) {
          $factorInput.val('');
        } else if (!$factorInput.val() && !$factorInput.is(':focus')) {
          $factorInput.val('1');
        }
      }
    }

    while (numberOfFormRows < reduction.getNumberOfFactorsOnPointSums()) {
      reduction.deleteFactorOnPointSum(
        reduction.getNumberOfFactorsOnPointSums() - 1,
      );
    }

    numberOfFormRows = formData.winnersInIntervals
      ? formData.winnersInIntervals.length
      : 0;
    const numberOfWinnersInIntervals =
      reduction.getNumberOfWinnersInIntervals();

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfWinnersInIntervals) {
        reduction.addWinnersInInterval();
      }

      const winnersInInterval = reduction.getWinnersInInterval(n);
      const winnersInIntervalData = formData.winnersInIntervals[n];

      winnersInInterval.setFromPoints(
        Utils.valueToNegativeBlank(winnersInIntervalData.fromPoints),
      );
      winnersInInterval.setToPoints(
        Utils.valueToNegativeBlank(winnersInIntervalData.toPoints),
      );
      winnersInInterval.setMaxNumberOfRaces(
        winnersInIntervalData.maxNumberOfRaces,
      );
      winnersInInterval.setMinNumberOfRaces(
        winnersInIntervalData.minNumberOfRaces,
      );
      winnersInInterval.setLegNumbers(winnersInIntervalData.legNumbers);
    }

    while (numberOfFormRows < reduction.getNumberOfWinnersInIntervals()) {
      reduction.deleteWinnersInInterval(
        reduction.getNumberOfWinnersInIntervals() - 1,
      );
    }

    numberOfFormRows = formData.misc ? formData.misc.length : 0;
    const numberOfMiscs = reduction.getNumberOfMiscs();

    for (n = 0; n < numberOfFormRows; n++) {
      if (n >= numberOfMiscs) {
        reduction.addMisc();
      }

      const misc = reduction.getMisc(n);
      const miscData = formData.misc[n];

      misc.setFromPoints(Utils.valueToNegativeBlank(miscData.fromPoints));
      misc.setToPoints(Utils.valueToNegativeBlank(miscData.toPoints));
      misc.setRequireMode(miscData.requireMode);
      misc.setType(miscData.type);
      misc.setLegNumbers(miscData.legNumbers);
    }

    while (numberOfFormRows < reduction.getNumberOfMiscs()) {
      reduction.deleteMisc(reduction.getNumberOfMiscs() - 1);
    }

    this.getAPI().scheduleBetUpdate();
  }

  onGridNumberInputChanged(position, value) {
    const viewIndex = this.getIndex();
    const betRow = this.getAPI().betInformation.getRow(position.row);

    betRow.setPointReductionPoints(
      position.column,
      viewIndex,
      Math.min(value, 999),
    );
    this.getAPI().scheduleBetUpdate();
  }
}
